import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assignment as OutcomeIcon,
  CancelOutlined as CancelIcon,
  CheckCircle as CheckCircleIcon,
  EventRepeat as RescheduleIcon,
  TaskAlt as TaskAltIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { AppointmentResponseDto, AppointmentStatus } from '../../../types/appointment.types';
import { UserAvatar } from '../../common/UserAvatar';
import { AppointmentCountdown } from '../AppointmentCountdown';

const BASE_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]:   'Очікує',
  [AppointmentStatus.CONFIRMED]: 'Підтверджено',
  [AppointmentStatus.CANCELLED]: 'Скасовано',
  [AppointmentStatus.COMPLETED]: 'Завершено',
};

function getExpertStatusLabel(a: AppointmentResponseDto): string {
  if (a.status === AppointmentStatus.PENDING) {
    if (a.createdBy === 'expert') return 'Чека підтвердження клієнта';
    if (a.createdBy === 'client') return 'Чека вашого підтвердження';
    return 'Очікує';
  }
  if (a.status === AppointmentStatus.CANCELLED) {
    if (a.cancelledBy === 'expert') return 'Скасовано вами';
    if (a.cancelledBy === 'client') return 'Скасовано клієнтом';
  }
  return BASE_STATUS_LABELS[a.status];
}

export const STATUS_COLORS: Record<AppointmentStatus, 'warning' | 'success' | 'error' | 'default'> = {
  [AppointmentStatus.PENDING]:   'warning',
  [AppointmentStatus.CONFIRMED]: 'success',
  [AppointmentStatus.CANCELLED]: 'error',
  [AppointmentStatus.COMPLETED]: 'default',
};

interface Props {
  appointments: AppointmentResponseDto[];
  loading: boolean;
  isMobile: boolean;
  showClient: boolean;
  onOpenDetails: (id: number) => void;
  onQuickConfirm: (id: number) => void;
  onOpenComplete: (appointment: AppointmentResponseDto) => void;
  onOpenReschedule: (appointment: AppointmentResponseDto) => void;
  onOpenCancel: (appointment: AppointmentResponseDto) => void;
}

export const ExpertAppointmentsTable: React.FC<Props> = ({
  appointments,
  loading,
  isMobile,
  showClient,
  onOpenDetails,
  onQuickConfirm,
  onOpenComplete,
  onOpenReschedule,
  onOpenCancel,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (appointments.length === 0) {
    return (
      <Typography sx={{ py: 6, textAlign: 'center' }} color="text.secondary">
        Записів не знайдено.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {showClient && <TableCell>Клієнт</TableCell>}
            <TableCell>Дата</TableCell>
            {!isMobile && <TableCell>Тривалість</TableCell>}
            <TableCell>Статус</TableCell>
            {!isMobile && <TableCell>Відгук</TableCell>}
            {!isMobile && <TableCell>Підсумок</TableCell>}
            <TableCell align="right">Дії</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appointments.map(a => (
            <TableRow hover key={a.id} onClick={() => onOpenDetails(a.id)} sx={{ cursor: 'pointer' }}>
              {showClient && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <UserAvatar path={a.client.avatar} name={a.client.name} sx={{ width: 36, height: 36 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.client.name}</Typography>
                  </Box>
                </TableCell>
              )}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{new Date(a.scheduledAt).toLocaleDateString('uk-UA')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(a.scheduledAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <AppointmentCountdown scheduledAt={a.scheduledAt} status={a.status} />
              </TableCell>
              {!isMobile && <TableCell>{a.durationMinutes} хв.</TableCell>}
              <TableCell>
                <Chip
                  label={getExpertStatusLabel(a)}
                  color={STATUS_COLORS[a.status]}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              {!isMobile && (
                <TableCell>
                  {a.review
                    ? <Rating value={a.review.rating} readOnly size="small" />
                    : <Typography variant="body2" color="text.disabled">—</Typography>
                  }
                </TableCell>
              )}
              {!isMobile && (
                <TableCell>
                  {a.outcome ? (
                    <Tooltip title={a.outcome.length > 120 ? a.outcome.slice(0, 120) + '…' : a.outcome} arrow>
                      <OutcomeIcon fontSize="small" color="action" sx={{ cursor: 'default' }} />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              )}
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {a.status === AppointmentStatus.PENDING && a.createdBy === 'client' && (
                    <Tooltip title="Підтвердити">
                      <IconButton size="small" color="primary" onClick={() => onQuickConfirm(a.id)}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {a.status === AppointmentStatus.CONFIRMED && (
                    <Tooltip title="Завершити">
                      <IconButton size="small" color="success" onClick={() => onOpenComplete(a)}>
                        <TaskAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* E1: reschedule лише для CONFIRMED. Для PENDING — confirm або cancel з причиною. */}
                  {a.status === AppointmentStatus.CONFIRMED && (
                    <Tooltip title="Перенести">
                      <IconButton size="small" color="primary" onClick={() => onOpenReschedule(a)}>
                        <RescheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(a.status === AppointmentStatus.PENDING || a.status === AppointmentStatus.CONFIRMED) && (
                    <Tooltip title="Скасувати">
                      <IconButton size="small" color="error" onClick={() => onOpenCancel(a)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Деталі">
                    <IconButton size="small" onClick={() => onOpenDetails(a.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
